// ============================================================================
// StatusPill — color-coded status badge
// ============================================================================
// Single source of truth for the pill-shaped status indicator used across
// case headers, commitment cards, action cards, and pool/case lists.
//
// Variants:
//   - solid    → filled background (use sparingly; reserve for active/primary)
//   - subtle   → tinted background + bordered text (default)
//   - outline  → no background, bordered text (compact lists)
//
// Tones drive the color set. Pick the closest semantic match:
//   - neutral  → grey (closed, draft, archived)
//   - info     → blue (in progress, scheduled)
//   - success  → green (recovered, honoured, on-track)
//   - warning  → amber (pending, attention, broken)
//   - danger   → red (escalated, dismissed, over-SLA)
//   - active   → primary blue with subtle glow (current/highlighted state)

import React from 'react';

export type PillTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'active';
export type PillVariant = 'solid' | 'subtle' | 'outline';
export type PillSize = 'xs' | 'sm' | 'md';

interface StatusPillProps {
  label: string;
  tone?: PillTone;
  variant?: PillVariant;
  size?: PillSize;
  /** Optional dot prefix (●). Pulses when `pulse` is true. */
  dot?: boolean;
  pulse?: boolean;
  /** Optional leading icon node. Sized by `size`. */
  icon?: React.ReactNode;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
}

const TONE_COLORS: Record<PillTone, { bg: string; bgSubtle: string; border: string; text: string; textSolid: string }> = {
  neutral: { bg: '#6b7280', bgSubtle: '#f3f4f6', border: '#9ca3af', text: '#4b5563',  textSolid: '#ffffff' },
  info:    { bg: '#2563eb', bgSubtle: '#dbeafe', border: '#60a5fa', text: '#1d4ed8',  textSolid: '#ffffff' },
  success: { bg: '#16a34a', bgSubtle: '#dcfce7', border: '#4ade80', text: '#15803d',  textSolid: '#ffffff' },
  warning: { bg: '#d97706', bgSubtle: '#fef3c7', border: '#fbbf24', text: '#b45309',  textSolid: '#ffffff' },
  danger:  { bg: '#dc2626', bgSubtle: '#fee2e2', border: '#f87171', text: '#b91c1c',  textSolid: '#ffffff' },
  active:  { bg: '#2563eb', bgSubtle: '#dbeafe', border: '#2563eb', text: '#1e40af',  textSolid: '#ffffff' },
};

const SIZE_TOKENS: Record<PillSize, { padding: string; fontSize: string; iconSize: number; gap: string; dotSize: string }> = {
  xs: { padding: '2px 7px',   fontSize: '0.65rem',  iconSize: 10, gap: '0.25rem', dotSize: '5px' },
  sm: { padding: '2px 9px',   fontSize: '0.7rem',   iconSize: 12, gap: '0.3rem',  dotSize: '6px' },
  md: { padding: '3px 11px',  fontSize: '0.78rem',  iconSize: 14, gap: '0.35rem', dotSize: '7px' },
};

const StatusPill: React.FC<StatusPillProps> = ({
  label, tone = 'neutral', variant = 'subtle', size = 'sm',
  dot, pulse, icon, title, className, style,
}) => {
  const colors = TONE_COLORS[tone];
  const tokens = SIZE_TOKENS[size];

  const palette = (() => {
    if (variant === 'solid') {
      return { background: colors.bg,        border: colors.bg,     color: colors.textSolid };
    }
    if (variant === 'outline') {
      return { background: 'transparent',    border: colors.border, color: colors.text };
    }
    // subtle (default)
    return   { background: colors.bgSubtle,  border: colors.border, color: colors.text };
  })();

  return (
    <span
      className={className}
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.gap,
        padding: tokens.padding,
        fontSize: tokens.fontSize,
        fontWeight: 600,
        lineHeight: 1.2,
        borderRadius: '999px',
        whiteSpace: 'nowrap',
        background: palette.background,
        border: `1px solid ${palette.border}`,
        color: palette.color,
        ...style,
      }}
    >
      {dot && (
        <span
          aria-hidden
          style={{
            display: 'inline-block',
            width: tokens.dotSize, height: tokens.dotSize, borderRadius: '50%',
            background: variant === 'solid' ? 'currentColor' : colors.bg,
            animation: pulse ? 'statusPillPulse 1.6s ease-in-out infinite' : undefined,
          }}
        />
      )}
      {icon}
      {label}
      {pulse && (
        <style>{`
          @keyframes statusPillPulse {
            0%,100% { opacity: 1; transform: scale(1); }
            50%     { opacity: 0.45; transform: scale(0.85); }
          }
        `}</style>
      )}
    </span>
  );
};

export default StatusPill;
